import { PixelRatio, Platform } from 'react-native';
import type {
  CloudinaryConfiguration,
  ImageTransform,
  ImageTransformOptions,
} from 'cloudinary-tiny-js';
import cloudinary from 'cloudinary-tiny-js';

/**
 * getCloudinaryUrl
 * Processes template image uri and cloudinary transform config into new cloudinary URL
 */
export const getCloudinaryUrl: HookType = (templateImage, transformConfig) => {
  const cl = cloudinary(CLOUDINARY_CONFIG);

  if (!transformConfig) {
    return templateImage;
  }

  // we set DPR setting inline to support dynamic pixel ratio change and inline mocking
  const pixelRatioSetting = { dpr: PixelRatio.get() };

  const transformOptions = {
    transformations: Array.isArray(transformConfig)
      ? amendWithDefaultTransforms([pixelRatioSetting, ...transformConfig])
      : { ...pixelRatioSetting, ...transformConfig },
  };

  return cl(templateImage, transformOptions as ImageTransformOptions);
};

//
// ─── TYPES ──────────────────────────────────────────────────────────────────────
//

type TransformArg = ImageTransform | readonly ImageTransform[];

type HookType = (t: string, o: TransformArg) => string;

// ─── UTILS ───────────────────────────────────────────────────────────────────
//

/**
 * This is intended to support `imageTransformDefaults` for array configs,
 * currently library do not support configs in array
 * @see {@link https://github.com/marnusw/cloudinary-tiny-js/issues/14}
 */
const amendWithDefaultTransforms = (
  transformations: readonly ImageTransform[],
): readonly ImageTransform[] => {
  return [DEFAULT_TRANSFORMS, ...transformations];
};

//
// ─── CONFIG ──────────────────────────────────────────────────────────────────

const DEFAULT_TRANSFORMS: ImageTransform = {
  // Since native is not considered browser, Cloudinary doesn't send back Webp
  // format back. We need to explicitly tell it to send the Webp format back.
  format: Platform.select({ web: 'auto', native: 'webp' }),
  quality: 'auto:good',
};

const CLOUDINARY_CONFIG: CloudinaryConfiguration = {
  /** The name of your Cloudinary account, a unique public identifier for URL
   * building and API access. */
  cloudName: 'sweetgreen',
  /**
   * The type of asset to deliver, `image` by default. Valid values: `image`,
   * `raw`, or `video`. Only the `image` type currently supports transforms.
   */
  assetType: 'image',
  /**
   * The storage or delivery type. Default: upload. For details on all possible
   * types, see the Cloudinary docs on delivery types.
   */
  deliveryType: 'fetch',
  /** Use https instead of http */
  secure: true,
  /** Transforms applied to all images, any transforms passed later will extend
   * these defaults. */
  imageTransformDefaults: DEFAULT_TRANSFORMS,
};
